import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Play, Pause, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const PlayVoiceButton = ({ messageId, audioPath, autoPlay = false }) => {
  const [state, setState] = useState("idle");
  const audioRef = useRef(null);
  const autoPlayed = useRef(false);

  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      URL.revokeObjectURL(audioRef.current.src);
    }
  }, []);

  const toggle = async (silent = false) => {
    if (state === "playing") {
      audioRef.current.pause();
      setState("idle");
      return;
    }
    if (!audioRef.current) {
      setState("loading");
      try {
        const r = await api.get(audioPath || `/messages/${messageId}/audio`, { responseType: "blob" });
        audioRef.current = new Audio(URL.createObjectURL(r.data));
        audioRef.current.onended = () => setState("idle");
      } catch {
        setState("idle");
        if (!silent) toast.error("Couldn't load India's voice right now");
        return;
      }
    }
    window.dispatchEvent(new CustomEvent("india-voice-play", { detail: messageId }));
    audioRef.current.onpause = () => window.dispatchEvent(new CustomEvent("india-voice-speaking", { detail: false }));
    try {
      await audioRef.current.play();
    } catch {
      setState("idle");
      if (!silent) toast.error("Tap again to play India's voice");
      return;
    }
    window.dispatchEvent(new CustomEvent("india-voice-speaking", { detail: true }));
    setState("playing");
  };

  useEffect(() => {
    if (autoPlay && !autoPlayed.current) {
      autoPlayed.current = true;
      toggle(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay]);

  useEffect(() => {
    const stopOthers = (e) => {
      if (e.detail !== messageId && audioRef.current) {
        audioRef.current.pause();
        setState("idle");
      }
    };
    window.addEventListener("india-voice-play", stopOthers);
    return () => window.removeEventListener("india-voice-play", stopOthers);
  }, [messageId]);

  const Icon = state === "loading" ? Loader2 : state === "playing" ? Pause : Play;
  return (
    <button
      onClick={() => toggle()}
      disabled={state === "loading"}
      data-testid={`play-voice-${messageId}`}
      className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-[#2A3B32] px-3 py-1 rounded-full border border-[#2A3B32]/15 hover:bg-[#2A3B32] hover:text-[#FDFBF7] transition-colors duration-200"
    >
      <Icon size={12} className={state === "loading" ? "animate-spin" : ""} />
      {state === "playing" ? "Pause" : state === "loading" ? "Loading…" : "Hear India"}
    </button>
  );
};

export default PlayVoiceButton;
